import * as React from 'react'
import * as SwitchPrimitive from 'primitives/switch'
import { tw } from 'utils/classnames'
import { Slot } from 'primitives/slot'
import { CheckIcon, ChevronRightIcon } from 'primitives/icons'

export function Switch(props: SwitchPrimitive.SwitchProps) {
  const theme = 'light'

  return (
    <SwitchPrimitive.Root
      {...props}
      className={tw(
        'relative h-[20px] w-[32px] rounded-full border-[1px] border-gray-700 bg-transparent outline-none',
        "data-[state='checked']:border-none data-[state='checked']:bg-violet-500",
        theme === 'light' && 'border-gray-700',
        props.className,
      )}
    >
      <SwitchPrimitive.Thumb
        className={tw(
          'bg-service-white block h-[16px] w-[16px] translate-x-[2px] rounded-full transition-transform duration-200 ease-in-out will-change-transform',
          "data-[state='checked']:translate-x-[14px]",
          theme === 'light' && "data-[state='checked']:bg-service-white bg-gray-700",
        )}
      />
    </SwitchPrimitive.Root>
  )
}

interface MenuRowProps {
  variant?: 'template' | 'template-second' | 'template-third'
  selected?: boolean
  selectedClassName?: string
}

const MenuRowWebsiteMenuRowRoot = function MenuRowWebsiteMenuRowRoot({
  ref,
  variant,
  selected,
  ...props
}: MenuRowProps & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      {...props}
      className={tw(
        'flex w-full items-center justify-start text-left font-medium text-gray-700 hover:text-gray-700',
        'rounded-2 mb-1 min-h-[40px] cursor-pointer pl-4 outline-none',
        selected
          ? 'text-service-white-white hover:text-service-white-white bg-violet-500'
          : 'hover:bg-gray-300 hover:text-gray-700',
        variant === 'template' && 'pl-2',
        variant === 'template-second' && 'pl-0',
        variant === 'template-third' && 'ml-[26px] w-[calc(100%-26px)]',
        props.className,
      )}
      ref={ref}
    />
  )
}

function MenuRowWebsiteMenuRowIcon(props: React.ComponentProps<'svg'>) {
  const Comp = Slot as unknown as 'svg'

  return <Comp {...props} className={tw('mr-2 h-[24px] w-[24px] shrink-0', props.className)} />
}

function MenuRowWebsiteMenuRightIcon(props: React.ComponentProps<'svg'>) {
  const Comp = Slot as unknown as 'svg'

  return <Comp {...props} className={tw('ml-2 mr-2 h-4 w-4 shrink-0', props.className)} />
}

function MenuRowWebsiteMenuRowArrow(props: React.ComponentProps<'svg'>) {
  return <ChevronRightIcon {...props} className={tw('ml-2 mr-2 h-4 w-[17px]', props.className)} />
}

function MenuRowWebsiteMenuRowCheck(props: React.ComponentProps<'svg'>) {
  return <CheckIcon {...props} className={tw('ml-2 mr-4 h-6 w-6 text-gray-600', props.className)} />
}

function MenuRowWebsiteMenuRowLink({
  variant,
  selected,
  selectedClassName,
  ...props
}: MenuRowProps & React.ComponentPropsWithoutRef<'a'>) {
  return (
    <a
      href={props.href || '#'}
      onClick={e => {
        if (props.onClick) {
          e.preventDefault()
          props.onClick(e)
        }
      }}
      className={tw(
        'flex w-full items-center justify-start text-left font-medium text-gray-200 hover:bg-gray-700 hover:text-gray-200',
        'rounded-2 mb-1 min-h-[40px] cursor-pointer pl-4 outline-none',
        selected &&
          tw(
            'text-service-white-white hover:text-service-white-white bg-violet-500 hover:bg-violet-500',
            selectedClassName,
          ),
        variant === 'template' && 'pl-2',
        variant === 'template-second' && 'pl-0',
        variant === 'template-third' && 'ml-[26px] w-[calc(100%-26px)]',
        props.className,
      )}
    >
      {props.children}
    </a>
  )
}

function MenuRowWebsiteMenuRowToggle(props: React.ComponentProps<typeof Switch>) {
  return <Switch {...props} />
}

export const MenuRowWebsiteMenu = Object.assign(MenuRowWebsiteMenuRowRoot, {
  Icon: MenuRowWebsiteMenuRowIcon,
  RightIcon: MenuRowWebsiteMenuRightIcon,
  Arrow: MenuRowWebsiteMenuRowArrow,
  Check: MenuRowWebsiteMenuRowCheck,
  Link: MenuRowWebsiteMenuRowLink,
  Toggle: MenuRowWebsiteMenuRowToggle,
})
